import React, { useState, useRef } from 'react'
import { ArrowLeft, Upload, Download, X, FileSpreadsheet, Table, Eye } from 'lucide-react'

interface KMLConverterProps {
  onBack: () => void
}

interface DataRow {
  [key: string]: string | number
}

const KMLConverter: React.FC<KMLConverterProps> = ({ onBack }) => {
  const [data, setData] = useState<DataRow[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [nameColumn, setNameColumn] = useState<string>('')
  const [latColumn, setLatColumn] = useState<string>('')
  const [lonColumn, setLonColumn] = useState<string>('')
  const [descriptionColumns, setDescriptionColumns] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string>('')
  const [showHtmlPreview, setShowHtmlPreview] = useState<boolean>(false)
  const [generatedHtml, setGeneratedHtml] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const parseCSV = (text: string): { headers: string[], rows: DataRow[] } => {
    const lines = text.trim().split('\n')
    if (lines.length < 2) {
      throw new Error('El archivo debe contener al menos una fila de encabezados y una fila de datos')
    }

    // Detectar separador (coma, punto y coma, o tabulador)
    const firstLine = lines[0]
    const separators = [',', ';', '\t']
    let separator = ','
    let maxColumns = 0

    for (const sep of separators) {
      const columns = firstLine.split(sep).length
      if (columns > maxColumns) {
        maxColumns = columns
        separator = sep
      }
    }

    const headers = lines[0].split(separator).map(h => h.trim())
    const rows: DataRow[] = []

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(separator).map(v => v.trim())
      if (values.length === headers.length && values.some(v => v !== '')) {
        const row: DataRow = {}
        headers.forEach((header, index) => {
          // Convertir números si es posible
          const value = values[index]
          if (value && !isNaN(Number(value)) && value !== '') {
            row[header] = Number(value)
          } else {
            row[header] = value
          }
        })
        rows.push(row)
      }
    }

    return { headers, rows }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setFileName(file.name.replace(/\.[^/.]+$/, ''))
    setError(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const { headers: parsedHeaders, rows } = parseCSV(text)
        
        if (rows.length === 0) {
          setError('No se encontraron filas de datos válidas en el archivo')
          return
        }

        setHeaders(parsedHeaders)
        setData(rows)

        // Auto-detectar columnas comunes
        const lowerHeaders = parsedHeaders.map(h => h.toLowerCase())
        
        // Detectar nombre
        const nameIndex = lowerHeaders.findIndex(h => 
          h.includes('nombre') || h.includes('name') || h.includes('id') || h.includes('clave') || h === 'n'
        )
        if (nameIndex >= 0) setNameColumn(parsedHeaders[nameIndex])

        // Detectar latitud
        const latIndex = lowerHeaders.findIndex(h => 
          h.includes('lat') || h.includes('y') || h === 'latitude' || h.includes('latitud')
        )
        if (latIndex >= 0) setLatColumn(parsedHeaders[latIndex])

        // Detectar longitud
        const lonIndex = lowerHeaders.findIndex(h => 
          h.includes('lon') || h.includes('lng') || h.includes('x') || h === 'longitude' || h.includes('longitud')
        )
        if (lonIndex >= 0) setLonColumn(parsedHeaders[lonIndex])

        // Incluir todas las columnas excepto coordenadas y nombre en descripción
        const defaultDescColumns = parsedHeaders.filter(h => 
          h !== parsedHeaders[nameIndex] && 
          h !== parsedHeaders[latIndex] && 
          h !== parsedHeaders[lonIndex]
        )
        setDescriptionColumns(defaultDescColumns)

      } catch (err) {
        setError(`Error al procesar el archivo: ${err instanceof Error ? err.message : 'Error desconocido'}`)
      }
    }

    reader.onerror = () => {
      setError('Error al leer el archivo')
    }

    reader.readAsText(file)
  }

  const generateTableHTML = (row: DataRow, index: number): string => {
    let html = `<table border="1" style="border-collapse:collapse; border-color:#FFFFFF;" cellpadding="3" cellspacing="0" width="400" style="font-size: 11px; font-family: Verdana, Arial, Helvetica, sans-serif;">`;
    
    // Fila de índice
    html += `<tr><td bgcolor="#0788DE" align="right"><font COLOR="#000000"><b># Registro</b></font></td><td bgcolor="#808080"> <font COLOR="#FFFFFF">${index + 1}</font></td></tr>`;
    
    // Filas para cada columna
    headers.forEach(header => {
      const value = row[header];
      if (value !== undefined && value !== null && value !== '') {
        html += `<tr><td bgcolor="#0788DE" align="right"><font COLOR="#000000"><b>${header}</b></font></td><td bgcolor="#808080"> <font COLOR="#FFFFFF">${value}</font></td></tr>`;
      }
    });
    
    html += `</table>`;
    return html;
  }

  const generateKML = (): string => {
    if (!nameColumn || !latColumn || !lonColumn) {
      throw new Error('Debe seleccionar las columnas de Nombre, Latitud y Longitud')
    }

    let kml = '<?xml version="1.0" encoding="UTF-8"?>\n'
    kml += '<kml xmlns="http://www.opengis.net/kml/2.2">\n'
    kml += '  <Document>\n'
    kml += `    <name>${fileName || 'Datos convertidos'}</name>\n`
    kml += '    <description>Generado por Herramientas HH</description>\n\n'

    // Estilo para los puntos
    kml += '    <Style id="defaultStyle">\n'
    kml += '      <IconStyle>\n'
    kml += '        <color>ff00ffff</color>\n'
    kml += '        <scale>1.0</scale>\n'
    kml += '        <Icon>\n'
    kml += '          <href>http://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png</href>\n'
    kml += '        </Icon>\n'
    kml += '      </IconStyle>\n'
    kml += '    </Style>\n\n'

    data.forEach((row, index) => {
      const name = row[nameColumn] || `Punto ${index + 1}`
      const lat = row[latColumn]
      const lon = row[lonColumn]

      // Validar coordenadas
      const latNum = parseFloat(lat as string)
      const lonNum = parseFloat(lon as string)
      
      if (isNaN(latNum) || isNaN(lonNum)) {
        return // Saltar esta fila si las coordenadas no son válidas
      }

      // Generar tabla HTML para la descripción
      const tableHTML = generateTableHTML(row, index)

      kml += '    <Placemark>\n'
      kml += `      <name><![CDATA[${name}]]></name>\n`
      kml += '      <styleUrl>#defaultStyle</styleUrl>\n'
      kml += '      <description><![CDATA[\n'
      kml += tableHTML
      kml += '      ]]></description>\n'
      kml += '      <Point>\n'
      kml += `        <coordinates>${lonNum},${latNum},0</coordinates>\n`
      kml += '      </Point>\n'
      kml += '    </Placemark>\n\n'
    })

    kml += '  </Document>\n'
    kml += '</kml>'

    return kml
  }

  const generateFullHTMLExport = (): string => {
    let html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Tablas Exportadas - ${fileName || 'Datos'}</title>
    <style>
        body { font-family: Verdana, Arial, Helvetica, sans-serif; font-size: 11px; margin: 20px; background-color: #f5f5f5; }
        .container { max-width: 1200px; margin: 0 auto; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 10px; margin-bottom: 20px; }
        h1 { margin: 0; }
        .table-container { background: white; padding: 20px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); margin-bottom: 20px; }
        table { border-collapse: collapse; border: 1px solid #FFFFFF; width: 100%; margin: 10px 0; }
        th { background-color: #0788DE; color: #000000; text-align: right; padding: 8px; border: 1px solid #FFFFFF; font-weight: bold; }
        td { background-color: #808080; color: #FFFFFF; padding: 8px; border: 1px solid #FFFFFF; }
        .info { background-color: #e8f4fd; border-left: 4px solid #0788DE; padding: 15px; margin: 20px 0; border-radius: 5px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>📊 Exportación de Tablas HTML</h1>
            <p>Archivo: ${fileName} | Registros: ${data.length} | Columnas: ${headers.length}</p>
        </div>
        
        <div class="info">
            <p><strong>Información:</strong> Estas tablas mantienen el mismo formato que el sistema original de Excel a KML.</p>
            <p>Generado automáticamente por Herramientas HH</p>
        </div>`;

    data.forEach((row, index) => {
      html += `
        <div class="table-container">
            <h2 style="color: #0788DE; margin-bottom: 15px;">Tabla #${index + 1}</h2>
            ${generateTableHTML(row, index)}
        </div>`;
    });

    html += `
        <div class="info">
            <p><strong>Total de tablas generadas:</strong> ${data.length}</p>
            <p><strong>Fecha de generación:</strong> ${new Date().toLocaleString()}</p>
        </div>
    </div>
</body>
</html>`;

    return html;
  }

  const handleDownloadKML = () => {
    try {
      setError(null)
      const kmlContent = generateKML()
      downloadFile(kmlContent, `${fileName || 'datos'}.kml`, 'application/vnd.google-earth.kml+xml')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar el archivo KML')
    }
  }

  const handleDownloadHTML = () => {
    try {
      setError(null)
      const htmlContent = generateFullHTMLExport()
      downloadFile(htmlContent, `${fileName || 'tablas'}.html`, 'text/html')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar el archivo HTML')
    }
  }

  const previewHTML = () => {
    try {
      setError(null)
      const htmlContent = generateFullHTMLExport()
      setGeneratedHtml(htmlContent)
      setShowHtmlPreview(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar la vista previa')
    }
  }

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const toggleDescriptionColumn = (column: string) => {
    setDescriptionColumns(prev => 
      prev.includes(column) 
        ? prev.filter(c => c !== column)
        : [...prev, column]
    )
  }

  const clearAll = () => {
    setData([])
    setHeaders([])
    setNameColumn('')
    setLatColumn('')
    setLonColumn('')
    setDescriptionColumns([])
    setError(null)
    setFileName('')
    setShowHtmlPreview(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Función para renderizar una fila de datos como tabla
  const renderDataRowAsTable = (row: DataRow, index: number) => {
    return (
      <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
        <table className="w-full border-collapse border border-gray-300" style={{fontSize: '11px', fontFamily: 'Verdana, Arial, Helvetica, sans-serif'}}>
          <tbody>
            <tr>
              <td className="bg-[#0788DE] text-right font-bold text-black p-2 border border-white"># Registro</td>
              <td className="bg-[#808080] text-white p-2 border border-white">{index + 1}</td>
            </tr>
            {headers.map(header => {
              const value = row[header]
              if (value !== undefined && value !== null && value !== '') {
                return (
                  <tr key={header}>
                    <td className="bg-[#0788DE] text-right font-bold text-black p-2 border border-white">{header}</td>
                    <td className="bg-[#808080] text-white p-2 border border-white">{value}</td>
                  </tr>
                )
              }
              return null
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver a Herramientas</span>
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Conversor de Base de Datos a KML
            </h1>
            <p className="text-lg text-slate-600">
              Convierte archivos CSV a formato KML con tablas HTML como en tu sistema original
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <X className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-red-400 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Modal para vista previa HTML */}
        {showHtmlPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-xl font-bold text-slate-900">Vista Previa HTML</h3>
                <button
                  onClick={() => setShowHtmlPreview(false)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-6">
                <iframe
                  srcDoc={generatedHtml}
                  title="Vista previa HTML"
                  className="w-full h-[60vh] border rounded-lg"
                  sandbox="allow-same-origin"
                />
              </div>
              <div className="p-6 border-t bg-slate-50">
                <button
                  onClick={() => {
                    downloadFile(generatedHtml, `${fileName || 'tablas'}.html`, 'text/html')
                    setShowHtmlPreview(false)
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-teal-600 transition-all"
                >
                  <Download className="w-5 h-5 inline mr-2" />
                  Descargar HTML Completo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Section */}
        {data.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Paso 1: Cargar archivo CSV
            </h2>
            <p className="text-slate-600 mb-6">
              Sube tu archivo CSV exportado desde Excel. El sistema mantendrá el formato de tablas HTML original.
            </p>

            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-blue-400 transition-colors bg-slate-50 hover:bg-blue-50">
                <Upload className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-lg text-slate-700 mb-2 font-medium">
                  Haga clic para seleccionar un archivo CSV
                </p>
                <p className="text-sm text-slate-500">
                  Exporta desde Excel como CSV (Valores separados por comas)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </label>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-slate-700 mb-2">
                <strong>Formato esperado del archivo:</strong>
              </p>
              <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                <li>Primera fila: encabezados de columnas</li>
                <li>Los datos pueden tener cualquier número de columnas</li>
                <li>El sistema detectará automáticamente las columnas de coordenadas</li>
                <li>Cada fila generará una tabla HTML con el formato original</li>
              </ul>
            </div>
          </div>
        )}

        {/* Configuration Section */}
        {data.length > 0 && (
          <div className="space-y-6">
            {/* File Info */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-semibold text-slate-900">
                      Archivo cargado: {fileName}
                    </p>
                    <p className="text-sm text-slate-600">
                      {data.length} registro{data.length !== 1 ? 's' : ''} • {headers.length} columna{headers.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearAll}
                  className="text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Limpiar
                </button>
              </div>
            </div>

            {/* Column Selection */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Paso 2: Configurar columnas
              </h2>
              <p className="text-slate-600 mb-6">
                Seleccione las columnas que corresponden a cada campo requerido:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Name Column */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Columna de Nombre/ID *
                  </label>
                  <select
                    value={nameColumn}
                    onChange={(e) => setNameColumn(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar...</option>
                    {headers.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>

                {/* Latitude Column */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Columna de Latitud *
                  </label>
                  <select
                    value={latColumn}
                    onChange={(e) => setLatColumn(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar...</option>
                    {headers.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>

                {/* Longitude Column */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Columna de Longitud *
                  </label>
                  <select
                    value={lonColumn}
                    onChange={(e) => setLonColumn(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar...</option>
                    {headers.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Column Selection Info */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Todas las columnas se incluirán automáticamente en las tablas HTML del KML, 
                  manteniendo el formato original que tenías en Excel.
                </p>
              </div>
            </div>

            {/* Preview of Tables */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Paso 3: Vista previa de tablas
                  </h2>
                  <p className="text-slate-600 mt-2">
                    Así se verán las tablas HTML en Google Earth ({Math.min(3, data.length)} de {data.length} tablas):
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={previewHTML}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Vista previa HTML
                  </button>
                  <button
                    onClick={handleDownloadHTML}
                    className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-2"
                  >
                    <Table className="w-4 h-4" />
                    Exportar HTML
                  </button>
                </div>
              </div>

              {/* Preview of tables */}
              <div className="space-y-4 max-h-[500px] overflow-y-auto p-4 border border-slate-200 rounded-lg">
                {data.slice(0, 3).map((row, index) => renderDataRowAsTable(row, index))}
                {data.length > 3 && (
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-600">
                      ... y {data.length - 3} tabla{data.length - 3 !== 1 ? 's' : ''} más
                    </p>
                  </div>
                )}
              </div>

              {/* Download Buttons */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleDownloadKML}
                  disabled={!nameColumn || !latColumn || !lonColumn}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                >
                  <Download className="w-6 h-6" />
                  <div className="text-left">
                    <div className="font-bold">Descargar KML</div>
                    <div className="text-sm font-normal opacity-90">Para Google Earth</div>
                  </div>
                </button>

                <button
                  onClick={handleDownloadHTML}
                  className="px-8 py-4 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-3 text-lg"
                >
                  <Table className="w-6 h-6" />
                  <div className="text-left">
                    <div className="font-bold">Descargar HTML</div>
                    <div className="text-sm font-normal opacity-90">Todas las tablas</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Information */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <span className="text-xl">✨</span> Características del sistema
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Formato KML mejorado:</h4>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Tablas HTML con el estilo original de tu sistema</li>
                <li>• Ícono de chincheta amarilla en Google Earth</li>
                <li>• Todas las columnas incluidas automáticamente</li>
                <li>• Compatible con cualquier número de columnas</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Funcionalidades extra:</h4>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Exportar todas las tablas como página HTML</li>
                <li>• Vista previa antes de descargar</li>
                <li>• Detección automática de columnas</li>
                <li>• Manejo flexible de datos</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default KMLConverter